import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Skeleton,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Person, Save, Cancel, Edit, Lock, Mail, Phone, Code } from "@mui/icons-material";
import { toTitleCase } from "../../../utils/toTitleCase";
import { camelCaseToWords } from "../../../utils/camelCaseToWords";
import useConfirmDialog from "../../../utils/useConfirmDialogue";
import _ from "lodash";
import biosenseAPI from "../../../api/biosense";
import useFeedbackDialog from "../../../utils/useFeedbackDialogue";

// Role mapping table
const ROLE_MAPPING = {
  admin: { value: 1, label: "Admin User", color: "error.main" },
  elevated: { value: 100, label: "Elevated User", color: "warning.main" },
  normal: { value: 101, label: "User", color: "primary.main" },
};

// Get user role information
function getUserRole(permissionValue) {
  if (typeof permissionValue !== "number") return { label: "Unknown", color: "text.secondary" };

  if (permissionValue < ROLE_MAPPING.elevated.value) {
    return ROLE_MAPPING.admin;
  } else if (permissionValue === ROLE_MAPPING.elevated.value) {
    return ROLE_MAPPING.elevated;
  } else {
    return ROLE_MAPPING.normal;
  }
}

const ProfileScreen = ({ setTopBarState }) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState({ field: null, value: null });
  const [emailVerifyCode, setEmailVerifyCode] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    error: "",
    loading: false,
  });
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [codeSentTime, setCodeSentTime] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const confirmAction = useConfirmDialog();
  const { showSuccess, showError } = useFeedbackDialog();
  const isDev = process.env.NODE_ENV === "development";

  // Load user data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await biosenseAPI.getUserProfile();
        setUser(response.user);
        setTopBarState({ firstname: response.user.firstname });
      } catch (error) {
        showError("Load Failed", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Verification code countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle edit mode toggle
  const handleEditToggle = (fieldName) => {
    if (editMode.field === fieldName) {
      // Check for changes
      if (_.isEqual(editMode.value, user[fieldName])) {
        setEditMode({ field: null, value: null });
        return;
      }

      // Confirm save changes
      confirmAction(
        {
          title: "Save Changes?",
          text: `Are you sure you want to save changes to ${camelCaseToWords(fieldName)}?`,
          icon: "question",
        },
        () => saveFieldChange(fieldName, editMode.value),
      );
    } else {
      // Enter edit mode
      setEditMode({ field: fieldName, value: user[fieldName] });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (_.isEqual(editMode.value, user[editMode.field])) {
      setEditMode({ field: null, value: null });
      return;
    }

    confirmAction(
      {
        title: "Discard Changes?",
        text: "Your changes will not be saved.",
        icon: "warning",
      },
      () => setEditMode({ field: null, value: null }),
    );
  };

  // Save field changes
  const saveFieldChange = async (fieldName, value) => {
    try {
      if (fieldName === "email") {
        // Email requires verification code
        if (!emailVerifyCode) {
          showError("Verification Failed", "Please obtain and enter the verification code first");
          return;
        }

        await biosenseAPI.updateEmail({
          code: parseInt(emailVerifyCode),
          newEmail: value,
        });
      } else {
        // Other fields update directly
        await biosenseAPI.updateUserProfile({
          field: fieldName,
          value: value,
        });
      }

      // Refresh user data after successful update
      const response = await biosenseAPI.getUserProfile();
      setUser(response.user);
      // 更新顶部栏的 firstname
      setTopBarState({ firstname: response.user.firstname });
      showSuccess("Update Successful", `${camelCaseToWords(fieldName)} has been updated`);

      // Reset edit state
      setEditMode({ field: null, value: null });
      if (fieldName === "email") {
        setEmailVerifyCode("");
        setVerificationCodeSent(false);
      }
    } catch (error) {
      showError("Update Failed", error.message);
    }
  };

  // Send email verification code
  const handleSendVerificationCode = async () => {
    try {
      await biosenseAPI.sendEmailUpdateCode();
      showSuccess(
        "Code Sent",
        "A verification code has been sent to your email. Please check your inbox.",
      );
      setVerificationCodeSent(true);
      setCodeSentTime(new Date());
      setCountdown(60); // 60-second countdown
    } catch (error) {
      showError("Send Failed", error.message);
    }
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Form validation
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordForm({ ...passwordForm, error: "All password fields are required" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordForm({ ...passwordForm, error: "New password and confirm password do not match" });
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordForm({
        ...passwordForm,
        error: "New password cannot be the same as the current password",
      });
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setPasswordForm({ ...passwordForm, error: "Password must be at least 8 characters long" });
      return;
    }

    try {
      setPasswordForm({ ...passwordForm, loading: true, error: "" });

      await biosenseAPI.updatePassword({
        currentPassword,
        newPassword,
      });

      showSuccess("Update Successful", "Your password has been successfully updated");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        error: "",
        loading: false,
      });
    } catch (error) {
      setPasswordForm({ ...passwordForm, error: error.message, loading: false });
    }
  };

  // Handle password form input
  const handlePasswordChange = (field, value) => {
    setPasswordForm({ ...passwordForm, [field]: value, error: "" });
  };

  // Handle edit value change
  const handleEditValueChange = (value) => {
    setEditMode({ ...editMode, value });
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ height: "100%", p: 1 }}>
        <Grid container columnSpacing={1} rowSpacing={1} sx={{ height: "100%" }}>
          <Grid item sm={12} md={5} sx={{ height: "100%" }}>
            <Card sx={{ height: "100%", boxShadow: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                  <Skeleton variant='circular' width={80} height={80} />
                  <Skeleton variant='text' sx={{ fontSize: "1.5rem", mt: 2 }} width='100%' />
                  <Skeleton variant='text' sx={{ fontSize: "1rem" }} width='80%' />
                </Box>
                <Divider sx={{ my: 1 }} />
                <List sx={{ p: 0 }}>
                  <ListItem sx={{ py: 1 }}>
                    <Skeleton variant='text' width='50%' />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item sm={12} md={7} sx={{ height: "100%" }}>
            <Card sx={{ height: "100%", boxShadow: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Skeleton variant='text' sx={{ fontSize: "1.2rem", mb: 2 }} width='30%' />
                <List sx={{ p: 0 }}>
                  {["email", "firstname", "lastname", "telephone"].map((fieldName) => (
                    <ListItem key={fieldName} sx={{ py: 1 }}>
                      <Grid container alignItems='center'>
                        <Grid item xs={8}>
                          <Skeleton variant='text' width='70%' />
                        </Grid>
                        <Grid item xs={4}>
                          <Skeleton variant='rectangular' height={32} width='40%' />
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 1 }} />
                <List sx={{ p: 0 }}>
                  {["currentPassword", "newPassword", "confirmPassword"].map((fieldName) => (
                    <ListItem key={fieldName} sx={{ py: 1 }}>
                      <Grid container alignItems='center'>
                        <Grid item xs={8}>
                          <Skeleton variant='text' width='70%' />
                        </Grid>
                        <Grid item xs={4}></Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Render field item
  const renderFieldItem = (fieldName, icon) => {
    const label = toTitleCase(camelCaseToWords(fieldName));
    const isEditing = editMode.field === fieldName;

    return (
      <ListItem key={fieldName} sx={{ py: 1 }}>
        <Grid container alignItems='center'>
          <Grid item xs={8}>
            <Box display='flex' gap={2} alignItems={"center"}>
              {icon && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "text.secondary",
                  }}
                >
                  {icon}
                </Box>
              )}
              <ListItemText
                sx={{ width: { lg: "14em" } }}
                primary={label}
                secondary={isEditing ? "" : user[fieldName]}
              />
              {isEditing && (
                <TextField
                  margin='dense'
                  required
                  fullWidth
                  id={fieldName}
                  name={fieldName}
                  autoComplete={fieldName}
                  autoFocus
                  value={editMode.value}
                  onChange={(e) => handleEditValueChange(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title='Cancel'>
                          <IconButton size='small' color='error' onClick={handleCancelEdit}>
                            <Cancel fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Save'>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={() => handleEditToggle(fieldName)}
                          >
                            <Save fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ),
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", justifyContent: "flex-end", columnGap: 1 }}>
            {!isEditing && (
              <Button
                variant='outlined'
                color='primary'
                onClick={() => handleEditToggle(fieldName)}
                size='small'
                startIcon={<Edit fontSize='small' />}
              >
                Edit
              </Button>
            )}
          </Grid>
        </Grid>
      </ListItem>
    );
  };

  return (
    <Box sx={{ height: "100%", p: 1 }}>
      {/* 使用CSS Grid实现等高布局 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 1,
          height: "100%",
          // md断点以上使用两列布局
          "@media (min-width: 900px)": {
            gridTemplateColumns: "5fr 7fr",
          },
        }}
      >
        {/* 左侧卡片 - 个人信息 */}
        <Box sx={{ height: "100%" }}>
          <Card sx={{ height: "100%", boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ width: 80, height: 80, mb: 2 }}>
                  <Person fontSize='large' />
                </Avatar>
                <Typography variant='h6' gutterBottom>
                  {`${user.firstname} ${user.lastname}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {user.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <List sx={{ p: 0 }}>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={"Role"}
                    secondary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            mr: 1,
                            bgcolor: getUserRole(user.role).color,
                          }}
                        />
                        {getUserRole(user.role).label}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
              {isDev && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button variant='contained' color='primary' size='small'>
                    Edit Profile
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* 右侧卡片 - 账户信息和密码管理 */}
        <Box sx={{ height: "100%" }}>
          <Card sx={{ height: "100%", boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant={"h6"} sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <Person sx={{ mr: 1 }} />
                Account Information
              </Typography>
              <List sx={{ p: 0 }}>
                {renderFieldItem("email", <Mail />)}

                {editMode.field === "email" && (
                  <ListItem sx={{ py: 1 }}>
                    <Grid container alignItems='center'>
                      <Grid item xs={8}>
                        <Box display='flex' gap={2} alignItems={"center"}>
                          <Code fontSize='small' sx={{ color: "text.secondary" }} />
                          <ListItemText
                            sx={{ width: { lg: "14em" } }}
                            primary={"Verification Code"}
                          />
                          <TextField
                            margin='dense'
                            required
                            fullWidth
                            id='emailVerifyCode'
                            name='emailVerifyCode'
                            autoComplete='emailVerifyCode'
                            value={emailVerifyCode}
                            onChange={(e) => setEmailVerifyCode(e.target.value)}
                            disabled={!verificationCodeSent}
                            placeholder={
                              verificationCodeSent
                                ? "Enter verification code"
                                : "Please obtain verification code first"
                            }
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={4}
                        sx={{ display: "flex", justifyContent: "flex-end", columnGap: 1 }}
                      >
                        <Button
                          variant='outlined'
                          color={verificationCodeSent ? "success" : "warning"}
                          onClick={handleSendVerificationCode}
                          size='small'
                          disabled={verificationCodeSent && countdown > 0}
                        >
                          {verificationCodeSent
                            ? countdown > 0
                              ? `Sent (${countdown}s)`
                              : "Resend"
                            : "Get Code"}
                        </Button>
                      </Grid>
                    </Grid>
                  </ListItem>
                )}

                {["telephone", "firstname", "lastname"].map((fieldName) =>
                  renderFieldItem(fieldName, fieldName === "telephone" ? <Phone /> : null),
                )}
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant={"h6"} sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <Lock sx={{ mr: 1 }} />
                Password Management
              </Typography>
              <List sx={{ p: 0 }}>
                <ListItem sx={{ py: 1 }}>
                  <Grid container alignItems='center'>
                    <Grid item xs={8}>
                      <Box display='flex' gap={2} alignItems={"center"}>
                        <ListItemText sx={{ width: { lg: "14em" } }} primary='Current Password' />
                        <TextField
                          margin='dense'
                          required
                          fullWidth
                          id='currentPassword'
                          name='currentPassword'
                          autoComplete='current-password'
                          type='password'
                          value={passwordForm.currentPassword}
                          onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
                          disabled={passwordForm.loading}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4}></Grid>
                  </Grid>
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <Grid container alignItems='center'>
                    <Grid item xs={8}>
                      <Box display='flex' gap={2} alignItems={"center"}>
                        <ListItemText sx={{ width: { lg: "14em" } }} primary='New Password' />
                        <TextField
                          margin='dense'
                          required
                          fullWidth
                          id='newPassword'
                          name='newPassword'
                          autoComplete='new-password'
                          type='password'
                          value={passwordForm.newPassword}
                          onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                          disabled={passwordForm.loading}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4}></Grid>
                  </Grid>
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <Grid container alignItems='center'>
                    <Grid item xs={8}>
                      <Box display='flex' gap={2} alignItems={"center"}>
                        <ListItemText
                          sx={{ width: { lg: "14em" } }}
                          primary='Confirm New Password'
                        />
                        <TextField
                          margin='dense'
                          required
                          fullWidth
                          id='confirmPassword'
                          name='confirmPassword'
                          autoComplete='confirm-password'
                          type='password'
                          value={passwordForm.confirmPassword}
                          onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                          disabled={passwordForm.loading}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: "right" }}>
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={handleChangePassword}
                        size='small'
                        disabled={passwordForm.loading}
                        startIcon={
                          passwordForm.loading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Lock fontSize='small' />
                          )
                        }
                      >
                        {passwordForm.loading ? "Processing..." : "Change Password"}
                      </Button>
                    </Grid>
                  </Grid>
                </ListItem>
                {passwordForm.error && (
                  <ListItem sx={{ py: 1 }}>
                    <Alert severity='error' sx={{ fontSize: "0.875rem", width: "100%" }}>
                      {passwordForm.error}
                    </Alert>
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileScreen;
